import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import {
  VerifiedUser as VerifiedIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import axios from 'axios';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In production (ondigitalocean.app or services.knls.in), use relative URL
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  // Development fallback
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const StudentVerification = () => {
  const [studentId, setStudentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleVerify = async () => {
    if (!studentId.trim()) {
      setError('Please enter a Student ID');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/students/verify/${studentId.trim()}`);
      
      if (response.data.success && response.data.verified) {
        setResult(response.data.student);
      } else {
        setError(response.data.message || 'Student ID not found');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to verify student ID');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
      <Box sx={{ maxWidth: '900px', mx: 'auto', px: 2 }}>
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
            p: 4,
            mb: 4,
            color: 'white',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
          }}
        >
          <VerifiedIcon sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
            Student Verification Portal
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Verify student enrollment and certificate details
          </Typography>
        </Box>

        {/* Search Card */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
              <TextField
                label="Enter Student ID"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value.toUpperCase())}
                placeholder="KNLS-STU-YYYYMMDD-XXX"
                fullWidth
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleVerify();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={handleVerify}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  },
                }}
              >
                Verify
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Verification Result */}
        {result && (
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              {/* Verified Badge */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 32 }} />
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  Verified Student
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  {/* Student ID */}
                  <Paper
                    sx={{
                      p: 2,
                      mb: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 1 }}>
                      Student ID
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                      {result.student_id}
                    </Typography>
                  </Paper>

                  {/* Photo */}
                  {result.photo_url && (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <Avatar
                        src={result.photo_url}
                        sx={{
                          width: 150,
                          height: 150,
                          mx: 'auto',
                          border: '4px solid #667eea',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 80 }} />
                      </Avatar>
                    </Box>
                  )}

                  {/* Personal Info */}
                  <Paper sx={{ p: 2, borderRadius: 2, bgcolor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PersonIcon color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        Personal Information
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Name
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {result.name}
                        </Typography>
                      </Box>
                      {result.father_name && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Father's Name
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {result.father_name}
                          </Typography>
                        </Box>
                      )}
                      {result.qualification && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Qualification
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {result.qualification}
                          </Typography>
                        </Box>
                      )}
                      {result.age && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Age
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {result.age} years
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  {/* Course Information */}
                  <Paper sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <SchoolIcon color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        Course Information
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Enrolled Courses
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mt: 0.5 }}>
                        {result.courses}
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Enrollment Details */}
                  <Paper sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CalendarIcon color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        Enrollment Details
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Enrollment Date
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {result.enrollment_date}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Enrollment Time
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {result.enrollment_time}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>

                  {/* Status */}
                  <Chip
                    label={result.status === 'active' ? 'Active Student' : result.status}
                    color={result.status === 'active' ? 'success' : 'default'}
                    icon={<CheckCircleIcon />}
                    sx={{ fontSize: '1rem', py: 3 }}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Footer Note */}
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> This Student ID is used for certificate issuance and can be verified
                  publicly. Keep this ID safe for future reference.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default StudentVerification;


